import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IFailedPayload } from 'redux/types/Call.types';
import {
  DataGridRequest,
  DataGridResponse,
  DownloadCallReportResponse,
  DownloadDigitalReportResponse,
  DownloadReportRequest,
  DownloadReportResponse,
  FilterRequest,
  FilterResponse,
  GetCallReportRequest,
  GetCallReportResponse,
  GetDigitalReportRequest,
  GetDigitalReportResponse,
  GetWhatsAppReportRequest,
  GetWhatsAppReportResponse,
  RequestCallReportRequest,
  RequestCallReportResponse,
  RequestDigitalReportRequest,
  RequestDigitalReportResponse,
  RequestWhatsAppReportRequest,
  RequestWhatsAppReportResponse,
} from 'redux/types/Reports.types';

export interface ReportsState {
  error: IFailedPayload | null;
  loading: boolean;
  filters: FilterResponse | null;
  dataGrid: DataGridResponse | null;
  downloadReportResponse: DownloadReportResponse | null;
  downloadCallReportResponse: DownloadCallReportResponse | null;
  requestCallReportResponse: RequestCallReportResponse | null;
  callReports: GetCallReportResponse | null;
  downloadDigitalReportResponse: DownloadDigitalReportResponse | null;
  requestDigitalReportResponse: RequestDigitalReportResponse | null;
  digitalReports: GetDigitalReportResponse | null;
  whatsappReports: GetWhatsAppReportResponse | null;
  requestWhatsAppReportResponse: RequestWhatsAppReportResponse | null;
  whatsappListLoading: boolean;
}

export const reportsInitState: ReportsState = {
  error: null,
  loading: false,
  filters: null,
  dataGrid: null,
  downloadReportResponse: null,
  downloadCallReportResponse: null,
  requestCallReportResponse: null,
  callReports: null,
  downloadDigitalReportResponse: null,
  requestDigitalReportResponse: null,
  digitalReports: null,
  whatsappReports: null,
  requestWhatsAppReportResponse: null,
  whatsappListLoading: false,
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState: reportsInitState,
  reducers: {
    fetchFilters: (state, action: PayloadAction<FilterRequest>) => {
      state.loading = true;
      state.error = null;
    },
    fetchFiltersSuccess: (state, action: PayloadAction<FilterResponse>) => {
      state.loading = false;
      state.filters = action.payload;
    },
    fetchFiltersFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchDataGrid: (state, action: PayloadAction<DataGridRequest>) => {
      state.loading = true;
      state.error = null;
    },
    fetchDataGridSuccess: (state, action: PayloadAction<DataGridResponse>) => {
      state.loading = false;
      state.dataGrid = action.payload;
    },
    fetchDataGridFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    downloadReport: (state, action: PayloadAction<DownloadReportRequest>) => {
      state.loading = true;
      state.error = null;
    },
    downloadReportSuccess: (
      state,
      action: PayloadAction<DownloadReportResponse>
    ) => {
      state.loading = false;
      state.downloadReportResponse = action.payload;
    },
    downloadReportFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    downloadCallReport: (state, action: PayloadAction<{ id: string }>) => {
      state.loading = true;
      state.error = null;
    },
    downloadCallReportSuccess: (
      state,
      action: PayloadAction<DownloadCallReportResponse>
    ) => {
      state.loading = false;
      state.downloadCallReportResponse = action.payload;
    },
    downloadCallReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    requestCallReport: (
      state,
      action: PayloadAction<RequestCallReportRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    requestCallReportSuccess: (
      state,
      action: PayloadAction<RequestCallReportResponse>
    ) => {
      state.loading = false;
      state.requestCallReportResponse = action.payload;
    },
    requestCallReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    getCallReports: (state, action: PayloadAction<GetCallReportRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getCallReportsSuccess: (
      state,
      action: PayloadAction<GetCallReportResponse>
    ) => {
      state.loading = false;
      state.callReports = action.payload;
    },
    getCallReportsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    downloadDigitalReport: (state, action: PayloadAction<{ id: string }>) => {
      state.loading = true;
      state.error = null;
    },
    downloadDigitalReportSuccess: (
      state,
      action: PayloadAction<DownloadDigitalReportResponse>
    ) => {
      state.loading = false;
      state.downloadDigitalReportResponse = action.payload;
    },
    downloadDigitalReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    requestDigitalReport: (
      state,
      action: PayloadAction<RequestDigitalReportRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    requestDigitalReportSuccess: (
      state,
      action: PayloadAction<RequestDigitalReportResponse>
    ) => {
      state.loading = false;
      state.requestDigitalReportResponse = action.payload;
    },
    requestDigitalReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    getDigitalReports: (
      state,
      action: PayloadAction<GetDigitalReportRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getDigitalReportsSuccess: (
      state,
      action: PayloadAction<GetDigitalReportResponse>
    ) => {
      state.loading = false;
      state.digitalReports = action.payload;
    },
    getDigitalReportsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    requestWhatsAppReport: (
      state,
      action: PayloadAction<RequestWhatsAppReportRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    requestWhatsAppReportSuccess: (
      state,
      action: PayloadAction<RequestWhatsAppReportResponse>
    ) => {
      state.loading = false;
      state.requestWhatsAppReportResponse = action.payload;
    },
    requestWhatsAppReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    getWhatsAppReports: (
      state,
      action: PayloadAction<GetWhatsAppReportRequest>
    ) => {
      state.whatsappListLoading = true;
      state.error = null;
    },
    getWhatsAppReportsSuccess: (
      state,
      action: PayloadAction<GetWhatsAppReportResponse>
    ) => {
      state.whatsappListLoading = false;
      state.whatsappReports = action.payload;
    },
    getWhatsAppReportsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.whatsappListLoading = false;
    },
    downloadWhatsAppReport: (state, action: PayloadAction<{ id: string }>) => {
      state.loading = true;
      state.error = null;
    },
    downloadWhatsAppReportSuccess: (state) => {
      state.loading = false;
    },
    downloadWhatsAppReportFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearDownloadReport: (state) => {
      state.downloadReportResponse = null;
    },
    resetDataGrid: (state) => {
      state.dataGrid = null;
    },
  },
});

export const {
  fetchFilters,
  fetchFiltersSuccess,
  fetchFiltersFailure,
  fetchDataGrid,
  fetchDataGridSuccess,
  fetchDataGridFailure,
  downloadReport,
  downloadReportSuccess,
  downloadReportFailure,
  downloadCallReport,
  downloadCallReportSuccess,
  downloadCallReportFailure,
  requestCallReport,
  requestCallReportSuccess,
  requestCallReportFailure,
  getCallReports,
  getCallReportsSuccess,
  getCallReportsFailure,
  downloadDigitalReport,
  downloadDigitalReportSuccess,
  downloadDigitalReportFailure,
  requestDigitalReport,
  requestDigitalReportSuccess,
  requestDigitalReportFailure,
  getDigitalReports,
  getDigitalReportsSuccess,
  getDigitalReportsFailure,
  clearDownloadReport,
  resetDataGrid,
  requestWhatsAppReport,
  requestWhatsAppReportSuccess,
  requestWhatsAppReportFailure,
  getWhatsAppReports,
  getWhatsAppReportsSuccess,
  getWhatsAppReportsFailure,
  downloadWhatsAppReport,
  downloadWhatsAppReportSuccess,
  downloadWhatsAppReportFailure,
} = reportsSlice.actions;

export default reportsSlice.reducer;
